import { Autocomplete } from '@react-google-maps/api'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import {
  Address,
  PackageStatus,
  PartnerDestination,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getAddressValidationFeedback,
  getPartners,
} from '@returnmates/client-core/src/selectors/admin'
import { getCurrentPackage } from '@returnmates/client-core/src/selectors/packages'
import { getCurrentUser } from '@returnmates/client-core/src/selectors/user'
import { getServicedZipCodes as getServicedZipCodesSelector } from '@returnmates/client-core/src/selectors/zipCode'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import { PickupDetails } from '@returnmates/client-core/src/type/pickups'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import formatPhone from '@returnmates/client-core/src/utils/formatPhone'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import AutocompleteInput from '@returnmates/ui-core/src/components/AutocompleteInput'
import Button from '@returnmates/ui-core/src/components/Button'
import Input from '@returnmates/ui-core/src/components/Input'
import SwaySpinner from '@returnmates/ui-core/src/components/SwaySpinner'
import { useGoogleValidation } from '@returnmates/ui-core/src/hooks/useGoogleValidation'
import clsx from 'clsx'
import { HTMLAttributes, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, useField } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FLYOUT_SIDEBAR_TYPES } from '../../constants/flyoutSidebar'
import AddNewAddress from './AddNewAddress'
import AddressesDropdown from './AddressesDropdown'
import useStyles from './styles'

interface Props {
  isReadOnly?: boolean
  userId?: string | null
  partnerCode?: string
  type?: string
}

function EditAddressFormInner({
  isReadOnly,
  userId,
  partnerCode,
  type = FLYOUT_SIDEBAR_TYPES.EDIT_ADDRESS,
}: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)
  const currentPackage = useSelector(getCurrentPackage)
  const partners = useSelector(getPartners)
  const servicedZipCodes = useSelector(getServicedZipCodesSelector)
  const addressValidationFeedback = useSelector(getAddressValidationFeedback)
  const { input: streetInput } = useField('address.street')
  const { input: stateInput } = useField('address.state')
  const { input: zipCodeInput } = useField('address.zipCode')
  const { input: instructionsInput } = useField('address.instructions')
  const { input: nameInput } = useField('address.name')
  const { input: phoneInput } = useField('address.phone')
  const { input: unitInput } = useField('address.unit')
  const { input: cityInput } = useField('address.city')
  const { input: originalUnitInput } = useField('address.originalUnit')
  const { input: originalCityInput } = useField('address.originalCity')
  const { input: originalStreetInput } = useField('address.originalStreet')
  const { input: originalStateInput } = useField('address.originalState')
  const { input: originalZipCodeInput } = useField('address.originalZipCode')
  const { input: addressIdInput } = useField('address.id')
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [isDestinationOpen, setIsDestinationOpen] = useState(false)

  const isGoogleValidationEnabled = useMemo(
    () =>
      type === FLYOUT_SIDEBAR_TYPES.EDIT_ADDRESS &&
      (currentPackage?.partner?.disabledAddressGeocoding ||
        currentPackage?.status === PackageStatus.PROBLEM),
    [currentPackage?.partner?.disabledAddressGeocoding, currentPackage?.status, type],
  )

  const {
    isValidatingAddressLoading,
    onLoad,
    onPlaceChanged,
    validateAddress,
    isValidateButtonEnabled,
    getHelperTextForRequiredField,
  } = useGoogleValidation({
    formName: 'address',
    withGoogleValidation: isGoogleValidationEnabled,
  })

  const onReset = useCallback(
    (value: Address) => {
      streetInput.onChange(value?.street || null)
      stateInput.onChange(value?.state || null)
      addressIdInput.onChange(value?.id || null)
      instructionsInput.onChange(value?.instructions || null)
      nameInput.onChange(value?.name || null)
      unitInput.onChange(value?.unit || null)
      zipCodeInput.onChange(value?.zipCode || null)
      cityInput.onChange(value?.city || null)
      phoneInput.onChange(value?.phone || null)
    },
    [
      streetInput,
      stateInput,
      addressIdInput,
      instructionsInput,
      nameInput,
      unitInput,
      phoneInput,
      cityInput,
      zipCodeInput,
    ],
  )

  const onChangeUserAddress = useCallback(
    (_, value) => {
      onReset(value)
    },
    [onReset],
  )

  const handleOpenAddressModal = useCallback(() => {
    setIsAddressModalOpen(true)
    setIsDestinationOpen(true)
  }, [])

  const handlePartnerDestinationOpen = useCallback(() => {
    setIsDestinationOpen(true)
  }, [])

  const handlePartnerDestinationClose = useCallback(() => {
    if (!isAddressModalOpen) {
      setIsDestinationOpen(false)
      setIsAddressModalOpen(false)
    }
  }, [isAddressModalOpen])

  const handleAddressDropdownClose = useCallback(() => {
    setIsDestinationOpen(false)
    setIsAddressModalOpen(false)
  }, [])

  const createUserAddress = useCallback(
    async (val: PickupDetails & { ignoreGeocoding: boolean }): Promise<Address> => {
      return await createAsyncAction(
        dispatch,
        actions.createUserAddress.request({
          userId,
          input: {
            name: val.name,
            phone: val.phone,
            street: val.street,
            unit: val.unit,
            city: val.city,
            state: val.state,
            zipCode: val.zipCode,
            instructions: val.instructions,
          },
        }),
      )
    },
    [dispatch, userId],
  )

  const createConsolidationAddress = useCallback(
    async (val: PickupDetails): Promise<Address> => {
      const res: PartnerDestination = await createAsyncAction(
        dispatch,
        actions.createConsolidationAddress.request({
          ...val,
          partnerCode,
          skipServicedArea: true,
        }),
      )

      return res.address
    },
    [dispatch, partnerCode],
  )

  const fetchUserAddresses = useCallback(async () => {
    if (type === FLYOUT_SIDEBAR_TYPES.EDIT_ADDRESS && userId && currentUser?.id !== userId) {
      try {
        await createAsyncAction(dispatch, actions.getUserById.request({ id: userId }))
      } catch (err) {
        const { message } = err as Error
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      }
    }
  }, [currentUser?.id, dispatch, type, userId])

  const consolidationPartner = useMemo(
    () => partners.find(p => p.code === partnerCode),

    [partners, partnerCode],
  )

  const partnerAddresses = useMemo(
    () =>
      consolidationPartner?.destinations?.map(
        (partnerDestination: PartnerDestination) => partnerDestination.address,
      ),
    [consolidationPartner?.destinations],
  )

  const getOptionalLabel = useCallback(
    (option: string) => {
      const addresses =
        type === FLYOUT_SIDEBAR_TYPES.EDIT_CONSOLIDATION_ADDRESS
          ? partnerAddresses
          : currentUser?.addresses

      const currentAddress = addresses?.reduce((acc, item: Address) => {
        if (item.id === option) {
          acc = `${item.street}, ${item.city}, ${item.state}, ${item.zipCode}`
        }

        return acc
      }, '')

      return currentAddress || ''
    },
    [currentUser?.addresses, partnerAddresses, type],
  )

  const getServicedZipCodes = useCallback(async () => {
    await createAsyncAction(dispatch, actions.getServicedZipCodes.request({ where: {} }))
  }, [dispatch])

  const copyToClipboard = useCallback(
    (filedName: string, fieldValue: string) => () => {
      navigator.clipboard.writeText(fieldValue || '')

      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.SUCCESS,
          message: `${filedName} was successfully copied to the clipboard`,
        }),
      )
    },
    [dispatch],
  )

  useEffect(() => {
    if (!Object.keys(servicedZipCodes).length) {
      getServicedZipCodes()
    }
  }, [servicedZipCodes, getServicedZipCodes])

  useEffect(() => {
    fetchUserAddresses()
  }, [fetchUserAddresses])

  if (isValidatingAddressLoading) {
    return <SwaySpinner classes={{ spinnerWrapper: classes.spinner }} />
  }

  return (
    <>
      <div className={classes.detailsRow}>
        <Field name="address.id">
          {({ input, meta }) => (
            <AutocompleteInput
              className={clsx(
                classes.addressesDropdown,
                isReadOnly && classes.disableAddressDropdown,
              )}
              options={
                type === FLYOUT_SIDEBAR_TYPES.EDIT_CONSOLIDATION_ADDRESS
                  ? partnerAddresses || []
                  : currentUser?.addresses || []
              }
              value={input.value || null}
              onChange={onChangeUserAddress}
              readOnly={isReadOnly}
              onOpen={handlePartnerDestinationOpen}
              onClose={handlePartnerDestinationClose}
              isOpen={isDestinationOpen}
              isOptionEqualToValue={(option: Address, value: string) => option.id === value}
              getOptionLabel={getOptionalLabel}
              renderOption={(events: HTMLAttributes<HTMLElement>, option: Address) => (
                <p {...events} key={option.id}>
                  {`${option.street}, ${option.city}, ${option.state}, ${option.zipCode}`}
                </p>
              )}
              placeholder="Selected Address"
              meta={meta}
              PaperComponent={options =>
                isAddressModalOpen ? (
                  <AddNewAddress
                    className={options.className}
                    onCloseDropdown={handleAddressDropdownClose}
                    createAddress={
                      type === FLYOUT_SIDEBAR_TYPES.EDIT_CONSOLIDATION_ADDRESS
                        ? createConsolidationAddress
                        : createUserAddress
                    }
                    reset={onReset}
                  />
                ) : (
                  <AddressesDropdown
                    className={options.className}
                    children={options.children}
                    onButtonClick={handleOpenAddressModal}
                  />
                )
              }
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Name</p>
        <Field name="address.name">
          {({ input, meta }) => (
            <Input
              placeholder="No assignee"
              classes={{
                textField: classes.input,
              }}
              disabled
              onChange={input.onChange}
              value={input.value}
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
              copyToClipboard={copyToClipboard}
              name="Name"
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Mobile</p>
        <Field name="address.phone">
          {({ input, meta }) => (
            <Input
              placeholder="Add number"
              classes={{
                textField: classes.input,
              }}
              disabled={isReadOnly}
              onChange={input.onChange}
              value={formatPhone(input.value)}
              fullWidth
              helperText={
                getHelperTextForRequiredField({ name: input.name, value: input.value }) ||
                meta.error
              }
              error={(meta.touched && meta.error) || !input?.value?.length}
              type="string"
              copyToClipboard={copyToClipboard}
              name="Phone"
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Street</p>
        <div className={classes.detailsSubRow}>
          <Autocomplete
            className={classes.googleAutocomplete}
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
          >
            <Field name="address.street">
              {({ input }) => (
                <Input
                  placeholder="Add street"
                  onChange={input.onChange}
                  value={input.value}
                  fullWidth
                  helperText={
                    getHelperTextForRequiredField({ name: input.name, value: input.value }) ||
                    addressValidationFeedback?.errors?.street[0]
                  }
                  error={Boolean(
                    addressValidationFeedback?.errors?.street.length || !input?.value?.length,
                  )}
                  classes={{
                    textField: classes.input,
                    input: isReadOnly ? classes.disabled : '',
                  }}
                  disabled={isReadOnly}
                  copyToClipboard={copyToClipboard}
                  name="Street"
                />
              )}
            </Field>
          </Autocomplete>
          {addressValidationFeedback?.isPlausible ? (
            <p className={classes.warningAddress}>
              Plausible, but may lead to issues with Onfleet task creation.
            </p>
          ) : null}
        </div>
      </div>
      {originalStreetInput.value && originalStreetInput.value !== streetInput.value ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Original Street</p>
          <Field name="address.originalStreet">
            {({ input }) => (
              <Autocomplete
                className={classes.googleAutocomplete}
                onLoad={onLoad}
                onPlaceChanged={onPlaceChanged}
              >
                <Input
                  placeholder="Add street"
                  onChange={input.onChange}
                  value={input.value}
                  fullWidth
                  classes={{
                    textField: classes.input,
                    input: classes.disabled,
                  }}
                  copyToClipboard={copyToClipboard}
                  name="Original Street"
                  disabled
                />
              </Autocomplete>
            )}
          </Field>
        </div>
      ) : null}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Unit</p>
        <div className={classes.detailsSubRow}>
          <Field name="address.unit">
            {({ input, meta }) => (
              <Input
                placeholder="Add unit"
                classes={{
                  textField: classes.input,
                }}
                disabled={isReadOnly}
                onChange={input.onChange}
                value={input.value}
                fullWidth
                helperText={meta.error}
                error={meta.touched && meta.error}
                copyToClipboard={copyToClipboard}
                name="Unit"
              />
            )}
          </Field>
        </div>
      </div>
      {originalUnitInput.value && originalUnitInput.value !== unitInput.value ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Original Unit</p>
          <Field name="address.originalUnit">
            {({ input }) => (
              <Input
                placeholder="Add unit"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                disabled
                fullWidth
                copyToClipboard={copyToClipboard}
                name="Original Unit"
              />
            )}
          </Field>
        </div>
      ) : null}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>City</p>
        <div className={classes.detailsSubRow}>
          <Field name="address.city">
            {({ input }) => (
              <Input
                placeholder="Add city"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                fullWidth
                helperText={
                  getHelperTextForRequiredField({ name: input.name, value: input.value }) ||
                  addressValidationFeedback?.errors?.city[0]
                }
                error={Boolean(
                  addressValidationFeedback?.errors?.city.length || !input?.value?.length,
                )}
                copyToClipboard={copyToClipboard}
                name="City"
                disabled={isReadOnly}
              />
            )}
          </Field>
        </div>
      </div>
      {originalCityInput.value && originalCityInput.value !== cityInput.value ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Original City</p>
          <Field name="address.originalCity">
            {({ input }) => (
              <Input
                placeholder="Add city"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                fullWidth
                copyToClipboard={copyToClipboard}
                name="Original City"
                disabled
              />
            )}
          </Field>
        </div>
      ) : null}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>State</p>
        <div className={classes.detailsSubRow}>
          <Field name="address.state">
            {({ input }) => (
              <Input
                placeholder="Add state"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                fullWidth
                helperText={
                  getHelperTextForRequiredField({ name: input.name, value: input.value }) ||
                  addressValidationFeedback?.errors?.state[0]
                }
                error={Boolean(
                  addressValidationFeedback?.errors?.state.length || !input?.value?.length,
                )}
                copyToClipboard={copyToClipboard}
                name="State"
                disabled={isReadOnly}
              />
            )}
          </Field>
        </div>
      </div>
      {originalStateInput.value && originalStateInput.value !== stateInput.value ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Original State</p>
          <Field name="address.originalState">
            {({ input }) => (
              <Input
                placeholder="Add state"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                fullWidth
                copyToClipboard={copyToClipboard}
                name="Original State"
                disabled
              />
            )}
          </Field>
        </div>
      ) : null}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Zip code</p>
        <Field name="address.zipCode">
          {({ input }) => (
            <Input
              placeholder="Add zip code"
              classes={{
                textField: classes.input,
              }}
              onChange={input.onChange}
              value={input.value}
              fullWidth
              helperText={
                getHelperTextForRequiredField({ name: input.name, value: input.value }) ||
                addressValidationFeedback?.errors?.zipCode[0]
              }
              error={Boolean(
                addressValidationFeedback?.errors?.zipCode.length || !input?.value?.length,
              )}
              type="number"
              max={99999}
              copyToClipboard={copyToClipboard}
              name="Zip code"
              disabled={isReadOnly}
            />
          )}
        </Field>
      </div>
      {originalZipCodeInput.value && originalZipCodeInput.value !== zipCodeInput.value ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Original Zip code</p>
          <Field name="address.originalZipCode">
            {({ input }) => (
              <Input
                placeholder="Add zip code"
                classes={{
                  textField: classes.input,
                }}
                onChange={input.onChange}
                value={input.value}
                fullWidth
                type="number"
                max={99999}
                copyToClipboard={copyToClipboard}
                name="Original Zip code"
                disabled
              />
            )}
          </Field>
        </div>
      ) : null}
      {isGoogleValidationEnabled && (
        <div className={classes.validateButtonContainer}>
          <Button
            className={clsx(classes.button, classes.saveButton)}
            onClick={validateAddress}
            label="Validate"
            disabled={!isValidateButtonEnabled}
          />
        </div>
      )}
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Instructions</p>
        <Field name="address.instructions">
          {({ input, meta }) => (
            <Input
              placeholder="Address instructions, i.e gate code"
              classes={{
                textField: classes.input,
              }}
              onChange={input.onChange}
              value={input.value}
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
              copyToClipboard={copyToClipboard}
              name="Instructions"
              disabled={isReadOnly}
            />
          )}
        </Field>
      </div>
    </>
  )
}

export default memo(EditAddressFormInner)
